import type { ReactNode } from "react";
import { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  DATE_OPERATIONS,
  getFreeformOperationsForValueType,
  NO_VALUE_OPERATIONS,
  NUMERIC_OPERATIONS,
  type FreeformFieldOption,
  type FreeformFilter,
  type FreeformOperation,
} from "@tellescope/utils";
import { cn } from "../lib/utils";
import { Button } from "./button";
import { Combobox, type ComboboxOption } from "./combobox";
import { DatePicker } from "./DateTimePicker";
import { Input } from "./input";
import { NumberInput } from "./NumberInput";
import { StringSelect } from "./string-select";

/**
 * Arguments handed to a consumer-supplied value renderer so it can render a custom value input
 * (e.g. a tag MultiSelect with free-form entry) in place of the row's built-in string/number/date
 * inputs. The renderer owns the value control and calls `onChange` with the next filter.
 */
export interface FilterConditionValueRenderProps {
  filter: FreeformFilter;
  field: FreeformFieldOption;
  onChange: (filter: FreeformFilter) => void;
}

export interface FilterConditionRowProps {
  filter: FreeformFilter;
  fieldOptions: FreeformFieldOption[];
  onChange: (filter: FreeformFilter) => void;
  onRemove: () => void;
  /**
   * Context-wide allow-list capping which operations may be used in this row, across all fields.
   * Intersected with each field's value-type defaults and its own `operations` list. Omit to allow
   * every operation a field's value type supports. Consumers that cannot support some operations
   * (e.g. only equality) pass a narrowed list here.
   */
  allowedOperations?: FreeformOperation[];
  /** Border/emphasis variant used to signal the block's active logic. Defaults to "and". */
  variant?: "and" | "or";
  /**
   * Optional override for the value input, keyed off the selected field. Return `undefined` to
   * fall back to the built-in inputs (string/number/date) for that field. Consumers use this to
   * inject a tag MultiSelect (with `creator` for free-form entry) for `valueType: "tags"` fields.
   */
  renderValueInput?: (props: FilterConditionValueRenderProps) => ReactNode | undefined;
  className?: string;
}

/**
 * One row of the reusable filter builder: a field select, an operation select, and a value input
 * (built-in, or a consumer-supplied override), with a remove button. Feature-agnostic — all
 * data-model knowledge is injected via `fieldOptions` and `renderValueInput`.
 */
export const FilterConditionRow = ({
  filter,
  fieldOptions,
  onChange,
  onRemove,
  allowedOperations,
  variant = "and",
  renderValueInput,
  className,
}: FilterConditionRowProps): ReactNode => {
  const { t } = useTranslation();
  const id = useId();

  const selectedField = fieldOptions.find((f) => f.value === filter.field);
  const valueType = selectedField?.valueType ?? "string";

  const availableOperations = useMemo(
    () =>
      getFreeformOperationsForValueType(valueType, {
        fieldOperations: selectedField?.operations,
        contextAllowed: allowedOperations,
      }),
    [valueType, selectedField?.operations, allowedOperations]
  );

  const showValueInput = !NO_VALUE_OPERATIONS.includes(filter.operation);
  const isDateOperation = DATE_OPERATIONS.includes(filter.operation);
  const isNumericOperation = NUMERIC_OPERATIONS.includes(filter.operation);

  const customValueInput = selectedField ? renderValueInput?.({ filter, field: selectedField, onChange }) : undefined;

  // The kind of value input an operation renders. Changing the operation only clears the value when
  // the kind changes (e.g. text -> date, or -> no input); switching between operations that share a
  // kind (e.g. equals -> does_not_equal) keeps the entered value. A custom value renderer owns its
  // own input, so its kind never changes with the operation — the value always survives.
  const builtInInputKind = (operation: FreeformOperation): "none" | "date" | "number" | "text" => {
    if (NO_VALUE_OPERATIONS.includes(operation)) return "none";
    if (DATE_OPERATIONS.includes(operation)) return "date";
    if (NUMERIC_OPERATIONS.includes(operation)) return "number";
    return "text";
  };

  const handleOperationChange = (operation: FreeformOperation) => {
    const valueSurvives =
      customValueInput !== undefined || builtInInputKind(operation) === builtInInputKind(filter.operation);
    onChange({ ...filter, operation, value: valueSurvives ? filter.value : undefined });
  };

  const fieldComboboxOptions = useMemo<ComboboxOption[]>(
    () => fieldOptions.map((f) => ({ value: f.value, label: f.label })),
    [fieldOptions]
  );
  const selectedFieldOption = fieldComboboxOptions.find((o) => o.value === filter.field);

  // With a single field option there is nothing to choose — render it as a static label rather than
  // a select, so the row reads "<Field> <operation> <value>".
  const isSingleField = fieldOptions.length === 1;

  return (
    <div
      className={cn(
        "flex items-start gap-2 rounded-md border bg-gray-50 px-2 py-2 w-full",
        variant === "or" && "border-orange-300",
        className
      )}
    >
      {isSingleField ? (
        <span className="text-xs font-light truncate shrink-0 px-1 h-8 flex items-center">
          {fieldOptions[0]?.label}
        </span>
      ) : (
        <Combobox
          value={selectedFieldOption}
          onChange={(selected) => {
            if (selected) {
              const nextField = fieldOptions.find((f) => f.value === selected.value);
              const nextOperation =
                getFreeformOperationsForValueType(nextField?.valueType ?? "string", {
                  fieldOperations: nextField?.operations,
                  contextAllowed: allowedOperations,
                })[0]?.value ?? "equals";
              onChange({ field: selected.value, operation: nextOperation, value: undefined });
            }
          }}
          options={fieldComboboxOptions}
          placeholder={t("Select field")}
          searchPlaceholder={t("Search fields...")}
          className="text-xs font-light h-8 flex-1 basis-0 min-w-0"
          popoverClassName="w-56"
          formatSelectedValue={(value) => <span className="font-light">{value.label}</span>}
          placeholderClassName="text-xs"
        />
      )}
      {/* Operation + value share the remaining width and wrap among themselves, so a wrapped value
          aligns under the operation (not under the field label). The remove button stays outside
          this group so it never wraps onto its own line. */}
      <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
        <StringSelect
          value={filter.operation}
          onValueChange={handleOperationChange}
          options={availableOperations}
          placeholder={t("Operation")}
          className="bg-white text-xs font-light h-8 flex-1 basis-24 min-w-0"
        />
        {showValueInput && (
          <div className="grow basis-40 min-w-0">
            {customValueInput !== undefined ? (
              customValueInput
            ) : isDateOperation ? (
              <DatePicker
                key={`${id}-date`}
                value={typeof filter.value === "number" ? new Date(filter.value) : undefined}
                onChange={(date) => onChange({ ...filter, value: date?.getTime() })}
                placeholder={t("Date")}
                inputProps={{ className: "bg-white" }}
              />
            ) : isNumericOperation ? (
              <NumberInput
                key={`${id}-number`}
                value={typeof filter.value === "number" ? filter.value : undefined}
                onChange={(val) => onChange({ ...filter, value: val })}
                placeholder={t("Value")}
                className="bg-white h-8 w-full"
              />
            ) : (
              <Input
                key={`${id}-text`}
                value={typeof filter.value === "string" ? filter.value : ""}
                onChange={(e) => onChange({ ...filter, value: e.target.value })}
                placeholder={t("Value")}
                className="bg-white text-xs md:text-xs h-8 w-full"
                size="small"
              />
            )}
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm-icon"
        className="text-muted-foreground shrink-0"
        onClick={onRemove}
        aria-label={t("Remove filter")}
      >
        <X size={4} />
      </Button>
    </div>
  );
};
